import { useTranslation } from 'react-i18next';
import '../../styles/pages/settings/settings.scss';
import { usePlanLock } from '../../lib/plan-lock/usePlanLock';
import type { PaymentConfirmation } from '../../lib/plan-lock/usePaymentConfirmation';
import MainSquareButton from '../../ui/buttons/MainSquareButton';

interface Props {
  /**
   * Set by a page the checkout returns to. While a payment is being confirmed
   * the notice says so instead of asking the member to choose a plan.
   */
  payment?: PaymentConfirmation;
}

/**
 * Shown on top of the subscription page while the organisation is locked:
 * why the rest of the app is closed, above the plans to choose from.
 */
const PlanLockNotice = ({ payment }: Props) => {
  const { t } = useTranslation();
  const { locked, endedPlanKind } = usePlanLock();

  if (!locked) {
    return null;
  }

  if (payment?.state === 'checking') {
    return (
      <div
        className='settingsInfoBanner'
        role='status'
        data-testid='plan-lock-payment-checking'
      >
        {t('PlanLock-CheckingPayment')}
      </div>
    );
  }

  if (payment?.state === 'processing') {
    return (
      <div
        className='settingsInfoBanner'
        role='status'
        data-testid='plan-lock-payment-processing'
      >
        <strong>{t('PlanLock-PaymentProcessingTitle')}.</strong>{' '}
        {t('PlanLock-PaymentProcessing')}{' '}
        <MainSquareButton
          value={t('PlanLock-CheckAgain')}
          onClick={payment.checkAgain}
          buttonType='light'
        />
      </div>
    );
  }

  return (
    <div
      className='settingsInfoBanner'
      role='status'
      data-testid='plan-lock-notice'
    >
      <strong>
        {endedPlanKind === 'paid'
          ? t('PlanLock-PlanEndedTitle')
          : endedPlanKind === 'trial'
          ? t('PlanLock-TrialEndedTitle')
          : t('PlanLock-NoPlanTitle')}
      </strong>{' '}
      {t('PlanLock-ChoosePlan')}
    </div>
  );
};

export default PlanLockNotice;
